import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import api from '@disco/api/server'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'
import {
  getCorePlan,
  getUsersQuota,
  getTracksQuota,
  getUser,
} from '@disco/state-management/selectors/user'
import { getSubscriptionPeriod } from '@disco/state-management/selectors/subscription'
import { chargebeePlanPriceLookup } from '@disco/utils/chargebee'
import ExpiredSubscription from './ExpiredSubscription'
import captureError from '@disco/utils/captureError'
import { formatCurrency } from '@disco/utils/pricing'

const ExpiredSubscriptionContainer = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const restartSubscription = useSelector(
    state => state.app.restartSubscription,
  )
  const userData = useSelector(getUser)
  const corePlan = useSelector(getCorePlan)
  const tracksQuota = useSelector(getTracksQuota)
  const usersQuota = useSelector(getUsersQuota)
  const subscription = useSelector(state => state.subscription)
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )
  const period = useSelector(getSubscriptionPeriod)
  const [estimatedTotalInCents, setEstimatedTotalInCents] = useState<
    number | null
  >(null)

  const [fetchEstimateError, setFetchEstimateError] = useState(false)

  useEffect(() => {
    dispatch(loadBusinessSubscription())
    dispatch(loadBillingProviderComponents())
  }, [dispatch])

  const fetchEstimate = useCallback(async () => {
    if (
      !billingProviderComponents.loaded ||
      !billingProviderComponents.chargebeePlans ||
      !subscription.loaded ||
      !period ||
      !corePlan?.id
    )
      return

    setFetchEstimateError(false)

    try {
      const chargebeePlan = chargebeePlanPriceLookup(
        billingProviderComponents.chargebeePlans,
        corePlan.id,
        period,
      )
      const addons = Object.values(billingProviderComponents.addons ?? {})
        .flat()
        .filter(a => a.subscription_quantity > 0)
        .map(a => ({ id: a.id, quantity: a.subscription_quantity }))

      const estimate = await api.chargebeeEstimateSignup({
        plan_id: chargebeePlan.id,
        addons,
        coupon: null,
      })
      setEstimatedTotalInCents(estimate.total)
    } catch (err) {
      captureError(err, {
        moduleName: 'ExpiredSubscriptionContainer',
        description: 'Failed to estimate restart subscription cost',
      })
      setFetchEstimateError(true)
    }
  }, [
    billingProviderComponents.addons,
    billingProviderComponents.chargebeePlans,
    billingProviderComponents.loaded,
    subscription.loaded,
    period,
    corePlan?.id,
  ])

  useEffect(() => {
    fetchEstimate()
  }, [fetchEstimate])

  const totalDue =
    estimatedTotalInCents != null ? formatCurrency(estimatedTotalInCents) : null

  const expiredSubscription =
    userData.business.private_metadata.expired_subscription
  useEffect(() => {
    if (!expiredSubscription) {
      navigate('/', { replace: true })
    }
  }, [expiredSubscription, navigate])

  const props = {
    users: usersQuota,
    files: tracksQuota,
    showUpdatePaymentDetails:
      Boolean(userData.business.chargebee_subscription_id) ||
      Boolean(userData.business?.private_metadata?.subscription_id),
    disabledUpdatePaymentDetails: !userData.permissions?.includes(
      'can_change_business_settings',
    ),
    subscription,
    planName: corePlan?.name || '',
    showConfirmModal: !restartSubscription,
    totalDue,
    fetchEstimateError,
    onRetryFetchEstimate: fetchEstimate,
  }
  return <ExpiredSubscription {...props} />
}

export default ExpiredSubscriptionContainer
